import { type MouseEvent, useCallback, useRef } from 'react';

import { useEditCanvasStore } from '../../../store';
import { type AppNode } from '../../../types';
import { type PasteGroup } from '../../clipboard/utils';
import { useCloneCanvasNodes } from '../../useCloneCanvasNodes';
import { createGroupsAfterPaste } from '../commands';
import { type Command } from '../types';
import { useCommandHistory } from './useCommandHistory';

/**
 * Handles alt+drag to duplicate nodes on the canvas.
 *
 * @returns Object with drag handlers
 * - onDragStart: If alt is held, clones the dragged nodes and adds them to the
 *   canvas at the original positions for immediate visual feedback.
 * - onDragStop: Creates an undo/redo command. Execute moves originals to their
 *   new positions and re-adds clones on redo. Undo removes clones and restores
 *   original positions. Returns true if alt+drag was active so the caller can
 *   skip the normal move command.
 */
export function useDuplicateByDragCommand() {
  const execute = useCommandHistory((state) => state.execute);
  const cloneObjects = useCloneCanvasNodes();
  const clonesRef = useRef<AppNode[] | null>(null);
  const groupsRef = useRef<PasteGroup[] | null>(null);

  const onDragStart = useCallback(
    (event: MouseEvent, nodes: { id: string }[]) => {
      if (!event.altKey) {
        clonesRef.current = null;
        groupsRef.current = null;
        return;
      }

      const { clones, groups } = cloneObjects(nodes);

      clonesRef.current = clones;
      groupsRef.current = groups;
      useEditCanvasStore.getState().addNodes(clones);

      // Optimistically assign temp groupIds so group controls show immediately
      for (const group of groups) {
        const memberIds = group.memberIndices.map((idx) => clones[idx].id);
        useEditCanvasStore.getState().setGroupId(memberIds, crypto.randomUUID());
      }
    },
    [cloneObjects],
  );

  const onDragStop = useCallback(
    (
      nodes: { id: string; position: { x: number; y: number } }[],
      previousPositions: { id: string; position: { x: number; y: number } }[],
    ) => {
      const clones = clonesRef.current;
      const groups = groupsRef.current;

      if (!clones) {
        return false;
      }

      clonesRef.current = null;
      groupsRef.current = null;

      const cloneIds = new Set(clones.map((c) => c.id));
      const newPosMap = new Map(nodes.map((n) => [n.id, n.position]));
      const prevPosMap = new Map(previousPositions.map((p) => [p.id, p.position]));

      const movedOriginals = [...newPosMap.entries()]
        .filter(([id]) => !cloneIds.has(id))
        .map(([id, position]) => ({ id, position }));

      const restoredOriginals = [...prevPosMap.entries()]
        .filter(([id]) => !cloneIds.has(id))
        .map(([id, position]) => ({ id, position }));

      const command: Command = {
        execute: () => {
          const { nodes } = useEditCanvasStore.getState();
          const hasClones = nodes.some((obj) => cloneIds.has(obj.id));

          if (movedOriginals.length > 0) {
            useEditCanvasStore.getState().moveNodes(movedOriginals);
          }
          if (!hasClones) {
            useEditCanvasStore.getState().addNodes(clones);

            // Undo removed temp groupIds — re-establish them before recreating server groups
            if (groups && groups.length > 0) {
              const groupLocalIds: string[][] = [];

              for (const group of groups) {
                const memberIds = group.memberIndices.map((idx) => clones[idx].id);
                groupLocalIds.push(memberIds);
                useEditCanvasStore.getState().setGroupId(memberIds, crypto.randomUUID());
              }

              createGroupsAfterPaste(groupLocalIds);
            }
          }
        },
        undo: () => {
          useEditCanvasStore.getState().removeNodes(clones.map((c) => c.id));
          if (restoredOriginals.length > 0) {
            useEditCanvasStore.getState().moveNodes(restoredOriginals);
          }
        },
      };

      execute(command);

      if (groups && groups.length > 0) {
        const groupLocalIds = groups.map((g) => g.memberIndices.map((idx) => clones[idx].id));
        createGroupsAfterPaste(groupLocalIds);
      }

      return true;
    },
    [execute],
  );

  return { onDragStart, onDragStop };
}
